import {
  type DatabaseField,
  type DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import { type ReactNode } from 'react';

import { type CellContent } from './cell-content.ts';

/**
 * Stable address for an initial, source-bound database read.
 */
export type FieldReadCellAddress = {
  readonly entryId: string;
  readonly fieldId: string;
};

/**
 * Initial field metadata and lazy source reads available to derived modules.
 * This is not an atomic source snapshot or a live publication protocol.
 */
export interface FieldReadDatabaseSnapshot {
  /**
   * Scoped to one resource-store lifetime; never a global database identity.
   */
  readonly key: string;
  readonly fieldIds: readonly string[];
  readonly entryIds: readonly string[];

  getField(fieldId: string): DatabaseField | undefined;
  getValue(address: FieldReadCellAddress): DatabaseValue | undefined;
  getDefaultValue(fieldId: string): DatabaseValue | undefined;
}

/**
 * One deduplicated, abortable external read declared by a field module.
 * Keys must include all load inputs and distinguish resource kinds. Services and
 * credentials are supplied by application composition, never acquired by views.
 */
export interface FieldReadResource<Value> {
  readonly key: string;
  load(signal: AbortSignal): Promise<Value | undefined>;
}

/**
 * Immutable state installed by the projection resource store.
 */
export type FieldReadResourceState<Value> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: Value }
  | { readonly status: 'unavailable' }
  | { readonly status: 'error' };

/**
 * Resource-dependent semantic resolution for one mounted cell.
 */
export interface CompiledFieldResourceRead<Content extends CellContent = CellContent> {
  readonly resources: readonly FieldReadResource<unknown>[];
  resolve(
    read: <Value>(resource: FieldReadResource<Value>) => FieldReadResourceState<Value>
  ): Content;
}

/**
 * Mount-time inputs available when a compiled field declares resources.
 */
export type FieldReadResourceInput = {
  readonly database: FieldReadDatabaseSnapshot;
  readonly address: FieldReadCellAddress;
  readonly storedValue: DatabaseValue | undefined;
  readonly defaultValue: DatabaseValue | undefined;
};

/**
 * Immutable address and values for one nested field presentation.
 *
 * The target owns no renderer or application state. A consumer resolves its
 * field through the same registry and database snapshot as a top-level cell.
 */
export type FieldReadNestedTarget = {
  readonly database: FieldReadDatabaseSnapshot;
  readonly field: DatabaseField;
  readonly address: FieldReadCellAddress;
  readonly storedValue: DatabaseValue | undefined;
  readonly defaultValue: DatabaseValue | undefined;
};

/**
 * Creates a shallowly immutable target for renderer-context composition.
 */
export function createFieldReadNestedTarget(
  target: FieldReadNestedTarget
): FieldReadNestedTarget {
  return Object.freeze({ ...target, address: Object.freeze({ ...target.address }) });
}

/**
 * Renderer-owned recursion boundary matching the existing field read limit.
 */
export const FIELD_READ_RECURSION_LIMIT = 10;

/**
 * Layout-neutral operations available to a compiled field renderer.
 */
export type CellRenderContext = {
  readonly renderNested: (target: FieldReadNestedTarget) => ReactNode;
};

/**
 * Renders semantic content owned by one field module.
 */
export type CellRenderer<Content extends CellContent = CellContent> = (
  content: Content,
  context?: CellRenderContext
) => ReactNode;

/**
 * Field settings compiled once per initial projection, then reused by mounted cells.
 * This captures settings only; lazy stored-value reads are not a coherent snapshot.
 */
export interface CompiledFieldRead<Content extends CellContent = CellContent> {
  readonly fieldId: string;

  /**
   * Static renderer paired with this reader by its field module.
   */
  render(content: Content, context?: CellRenderContext): ReactNode;

  /**
   * Resolves one available stored value into renderer-neutral cell content.
   * `undefined` means confirmed absence, never unloaded, loading or failed data.
   * The module preserves the field type's existing absent-value/default semantics.
   */
  resolve(
    storedValue: DatabaseValue | undefined,
    defaultValue: DatabaseValue | undefined
  ): Content;

  /**
   * Declares bounded external reads for a mounted value. Direct fields omit
   * this method and retain the synchronous allocation-minimal path above.
   */
  readonly getResourceRead?: (
    input: FieldReadResourceInput
  ) => CompiledFieldResourceRead<Content> | undefined;
}

/**
 * Defines how one database field type compiles its settings for read-only rendering.
 */
export interface FieldReadModule<Content extends CellContent = CellContent> {
  readonly fieldType: DatabaseFieldType;

  /**
   * Captures the field settings needed to resolve values without application state.
   */
  compile(field: DatabaseField): CompiledFieldRead<Content>;
}

type TypedCompiledFieldRead<Content extends CellContent> = Omit<
  CompiledFieldRead<Content>,
  'render'
>;

/**
 * Defines a field module while keeping its resolver and renderer content types paired.
 *
 * The registry erases that private content type only after the pair is assembled,
 * so adding a module does not require changing a global content union or renderer.
 */
export function defineFieldReadModule<Content extends CellContent>(definition: {
  readonly fieldType: DatabaseFieldType;
  readonly compile: (field: DatabaseField) => TypedCompiledFieldRead<Content>;
  readonly render: CellRenderer<Content>;
}): FieldReadModule<Content> {
  return Object.freeze({
    fieldType: definition.fieldType,
    compile(field: DatabaseField): CompiledFieldRead<Content> {
      const compiled = definition.compile(field);

      return Object.freeze({
        ...compiled,
        render: definition.render
      });
    }
  });
}

/**
 * Explicit registry used to select a field read module by database field type.
 */
export interface FieldReadRegistry {
  /**
   * Compiles a supported field, or returns undefined when no reader is registered.
   * Reader registration is not module-mode policy or write authorization.
   */
  compile(field: DatabaseField): CompiledFieldRead | undefined;
}

/**
 * Creates an immutable field-module registry.
 */
export function createFieldReadRegistry(
  modules: readonly FieldReadModule[]
): FieldReadRegistry {
  const modulesByType = new Map(
    modules.map((module) => [module.fieldType, module] as const)
  );

  return Object.freeze({
    compile(field: DatabaseField): CompiledFieldRead | undefined {
      return modulesByType.get(field.type)?.compile(field);
    }
  });
}
